import { ApplicationElement, html } from '@crossiety/lit-components';

import '@crossiety/lit-components/components/icon/element.js';
import CommonActions from '@crossiety/lit-components/components/common_actions/element.js';

import translations from './translations.js';
import style from './style.lit.css';

/**
 * Component for wrapping content with functionality to scroll to specific element and remove itself from the DOM
 *
 * @prop anchorEl - For scrolling to specific element when action box clicked (format is the same one we would use in `document.querySelector(...)`)
 * @prop confirmRemoveMessage - Message to show in confirm dialog when removing. If not specified, we won't show the dialog (optional)
 *
 * @slot default - For action box content
 * @slot close-action - For close button
 */

export default class ActionBox extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    anchorEl: { type: String },
    confirmRemoveMessage: { type: String },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();

    /** @internal */
    this.commonActions = new CommonActions(this);
  }

  /** @internal */
  get target() {
    return document.querySelector(this.anchorEl);
  }

  /** @internal */
  get reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  handleAnchorClick() {
    this.target?.scrollIntoView({
      behavior: this.reduceMotion ? 'auto' : 'smooth',
    });
  }

  handleRemoveClick(e) {
    if (
      this.confirmRemoveMessage &&
      !this.commonActions.confirmDialog(this.confirmRemoveMessage)
    )
      return e.preventDefault();

    this.remove();
  }

  renderAnchorLink() {
    return html`
      <button
        aria-label=${this.t('anchor_label')}
        class="xs-btn xs-btn-link"
        type="button"
        @click=${this.handleAnchorClick}>
        <ef-icon name="angle-down"></ef-icon>
      </button>
    `;
  }

  render() {
    return html`
      <slot @click=${this.handleRemoveClick} name="close-action"></slot>
      <slot></slot>
      ${this.anchorEl ? this.renderAnchorLink() : null}
    `;
  }
}

ActionBox.registerTagName('xs-action-box');

customElements.get('xs-action-box') ||
  customElements.define('xs-action-box', ActionBox);
